// useRatingModal.ts
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import {
  closeRatingModal,
  openRatingModal,
} from "../../../store/slices/commonModalSlice";
import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import { selectSaleItemById } from "../../../store/slices/saleItemListSlice";
import { RatingValue } from "../../types/enums";
import { getImageUri } from "../../utils/functions/getImageUri";
import { formatSaleItemName } from "../../utils/functions/formatSaleItemName";
import { useItemDataUpdater } from "../../hooks/useItemDataUpdater";

export const useRatingModal = () => {
  const dispatch = useDispatch();
  const { updateRating } = useItemDataUpdater();
  const currentUser = useSelector(selectCurrentUser);

  const { isOpen, itemId } = useSelector(
    (state: RootState) => state.commonModal.ratingModal,
  );

  const item = useSelector((state: RootState) =>
    itemId ? selectSaleItemById(state, itemId) : null,
  );

  const itemImage = item?.files?.[0]
    ? `${getImageUri()}${item.files[0]}`
    : null;

  const itemName = item ? formatSaleItemName(item) : "";

  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const openModal = (itemId: number, currentRating?: RatingValue) => {
    dispatch(openRatingModal({ itemId }));

    if (currentRating !== undefined) {
      setRating(currentRating + 1);
    } else {
      setRating(0);
    }

    setComment("");
  };

  const closeModal = () => {
    dispatch(closeRatingModal());
    setRating(0);
    setComment("");
  };

  const submitRating = async () => {
    if (rating === 0 || !itemId || !currentUser) return false;

    const apiRating = (rating - 1) as RatingValue;

    setIsLoading(true);

    try {
      updateRating(
        itemId,
        Number(currentUser.id),
        apiRating,
        comment.trim() || undefined,
      );
      closeModal();
      return true;
    } catch (error) {
      console.error("Ошибка при добавлении оценки:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingRating = (itemRatings?: any[]) => {
    if (!itemRatings || !currentUser) {
      setRating(0);
      setComment("");
      return;
    }

    const userRating = itemRatings.find((r) => r.userId === currentUser.id);
    if (userRating) {
      setRating(userRating.rating + 1);
      setComment(userRating.comment || "");
    } else {
      setRating(0);
      setComment("");
    }
  };

  return {
    isOpen,
    itemId,
    itemName, // ← уже отформатированное имя с параметрами
    itemImage,
    rating,
    comment,
    isLoading,
    isRatingSelected: rating > 0,
    setRating,
    setComment,
    openModal,
    closeModal,
    submitRating,
    loadExistingRating,
    handleClose: closeModal,
    handleSubmit: submitRating,
  };
};
